/*
  clikt. — option rows that show the choice, not the whole menu.

  Each option collapses to a single line naming what is selected. "View all"
  opens a drawer from the right showing every value as a picture, because a
  finish or a size is something you look at rather than read. Picking one
  closes the drawer and selects the real radio underneath.

  The radios are never touched or removed. Shopify still resolves the variant
  from them, Dawn still re-renders the price and the buy button off them, and
  the ready-to-ship toggle still drives them. This only changes how many of
  them are on screen at once.
*/
(function () {
  'use strict';

  var drawer = null;

  /* --- the inline row --------------------------------------------------- */

  function label(fieldset) {
    return fieldset.querySelector('[data-clikt-opt-value]');
  }

  function selectedText(fieldset) {
    var checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!checked) return '';
    var lbl = fieldset.querySelector('label[for="' + checked.id + '"]');
    if (lbl) {
      /*
        The label carries a screen-reader only "Variant sold out or
        unavailable" note. Reading textContent straight off it printed that
        after every size. Copy the label, drop the hidden parts, then read.
      */
      var copy = lbl.cloneNode(true);
      var hidden = copy.querySelectorAll('.visually-hidden, [class*="visually-hidden"]');
      for (var i = 0; i < hidden.length; i++) hidden[i].remove();
      return (copy.textContent || '').replace(/\s+/g, ' ').trim();
    }
    return checked.value || '';
  }

  /*
    Only ever writes when the text actually differs.

    Writing unconditionally is what froze the page: the observer below fires
    on any DOM change, sync replaced the text node with an identical one,
    that counted as a change, and round it went.
  */
  function sync(fieldset) {
    var el = label(fieldset);
    if (!el) return;
    var text = selectedText(fieldset);
    if (text && el.textContent.trim() !== text) el.textContent = text;
  }

  function optionName(fieldset) {
    var el = fieldset.querySelector('.clikt-opt__name');
    return el ? el.textContent.trim() : '';
  }

  /* Every value in the option, read straight off the radios. */
  function valuesOf(fieldset) {
    var inputs = fieldset.querySelectorAll('input[type="radio"]');
    var out = [];
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var lbl = fieldset.querySelector('label[for="' + input.id + '"]');
      var name = input.value;
      /*
        Two different systems mark a value as not buyable right now:
        Dawn's own disabled classes, and the ready-to-ship module's
        "clikt-out-of-lane" (the value exists, just not in the lane —
        e.g. only 140cm / 2-Pack Painted is stocked for dispatch).
        The drawer treats both the same way.
      */
      var soldOut = input.disabled ||
        /disabled|sold-out|clikt-out-of-lane/.test(input.className);
      if (lbl && /disabled|sold-out|clikt-out-of-lane/.test(lbl.className)) soldOut = true;
      out.push({ name: name, id: input.id, checked: input.checked, soldOut: soldOut });
    }
    return out;
  }

  /*
    What each material actually gives you, in Dale's words. Lifted from the
    3D builder so the drawer and the builder say the same thing about the
    same finish rather than drifting apart.

    Matched loosely on the value name, because the Shopify option values
    are written for the catalogue and the builder uses short codes.
  */
  var FINISH_NOTES = [
    [/\bmdf\b/, 'Strong, smooth and great for DIY painting.'],
    [/plywood|\bply\b/, 'Lightweight, durable and a great natural look.'],
    [/timber laminate|finawood/, 'Durable, knock and moisture resistant for heavy activation use. Wipe up moisture straight away.'],
    [/colour laminate|color laminate|satin laminate|velvet/, 'Durable, knock and moisture resistant for heavy activation use. Wipe up moisture straight away.'],
    [/solid ash|\bash\b/, 'Solid Timber Ash hardwood, 33mm, with a clear coat finish.'],
    [/2\s*-?\s*pack|2\s*pac\b|paint/, 'Premium painted finish for standout brands.'],
  ];

  function noteFor(value) {
    var v = String(value || '').toLowerCase();
    for (var i = 0; i < FINISH_NOTES.length; i++) {
      if (FINISH_NOTES[i][0].test(v)) return FINISH_NOTES[i][1];
    }
    return '';
  }
  function readJson(selector) {
    var el = document.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /*
    Pictures for one option's values.

    Two sources, in order of how much they actually know:

    1. The recovered Squarespace map, which held a photo per option
       COMBINATION and so can tell 3000K from 6000K.
    2. Shopify variant images, which is one photo per variant. Useful for
       finish on the products the map does not cover.

    The second source is checked before it is trusted. A Shopify variant
    carries a single image, so asking it "show me each lighting" returns the
    same photo every time — three identical tiles, which reads as a page that
    failed to load rather than a choice. Where every value lands on the same
    picture the images are dropped and the drawer falls back to text tiles,
    which is the honest answer.
  */
  function imagesFor(name) {
    var map = readJson('[data-clikt-option-images-map]');
    if (map && map[name] && Object.keys(map[name]).length) return map[name];

    var all = readJson('[data-clikt-option-images]');
    var fromVariants = (all && all[name]) || {};

    var urls = Object.keys(fromVariants)
      .map(function (k) { return fromVariants[k]; })
      .filter(Boolean);
    var distinct = {};
    urls.forEach(function (u) { distinct[u] = true; });
    if (urls.length > 1 && Object.keys(distinct).length < 2) return {};

    return fromVariants;
  }

  /* --- the drawer ------------------------------------------------------- */

  function buildDrawer() {
    if (drawer) return drawer;
    drawer = document.createElement('div');
    drawer.className = 'clikt-drawer';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.innerHTML =
      '<div class="clikt-drawer__scrim" data-clikt-drawer-close></div>' +
      '<aside class="clikt-drawer__panel" role="dialog" aria-modal="true" aria-label="Choose an option">' +
        '<header class="clikt-drawer__bar">' +
          '<h2 class="clikt-drawer__title" data-clikt-drawer-title></h2>' +
          '<button type="button" class="clikt-drawer__close" data-clikt-drawer-close aria-label="Close">&times;</button>' +
        '</header>' +
        '<div class="clikt-drawer__body" data-clikt-drawer-body></div>' +
      '</aside>';
    document.body.appendChild(drawer);

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-clikt-drawer-close]')) { closeDrawer(); return; }
      var info = e.target.closest('[data-clikt-note]');
      if (info) {
        var blurb = info.nextElementSibling;
        var open = info.getAttribute('aria-expanded') === 'true';
        info.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (blurb) blurb.hidden = open;
        return;
      }
      var tile = e.target.closest('[data-clikt-choose]');
      if (!tile) return;
      choose(tile.getAttribute('data-clikt-choose'));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    return drawer;
  }

  var activeFieldset = null;

  function choose(inputId) {
    var input = document.getElementById(inputId);
    if (!input) return;
    /*
      Click, not .checked = true. Dawn listens for the change event on the
      picker to re-resolve the variant, and setting the property fires
      nothing at all.
    */
    input.click();
    closeDrawer();
  }

  function openDrawer(fieldset) {
    activeFieldset = fieldset;
    var d = buildDrawer();
    var name = optionName(fieldset);
    /*
      Size is always the plain list. A size is a number you read, not a thing
      you look at — and only some products happen to carry per-size photos
      (the illuminated cart did), so image tiles here made one product's Size
      drawer look nothing like the rest of the store.
    */
    var images = /size/i.test(name) ? {} : imagesFor(name);
    var values = valuesOf(fieldset);

    d.querySelector('[data-clikt-drawer-title]').textContent = name;

    /*
      Values the current combination cannot buy (e.g. Ready to dispatch only
      exists in one finish) are treated two ways, per Dale:

      - Size: keep them visible but crossed out, so the range still reads.
      - Everything else (finishes, lighting): drop them from the drawer
        entirely — an out-of-stock finish tile just looks broken.

      This reads off the radios Dawn already disables, so it changes NOTHING
      for the configurator or made-to-order, where every value stays live.
      The selected value is always kept, whatever its state.
    */
    if (!/size/i.test(name)) {
      values = values.filter(function (v) { return !v.soldOut || v.checked; });
    }

    var withImages = values.filter(function (v) { return images[v.name]; }).length;
    var html = '<div class="clikt-drawer__grid' + (withImages ? '' : ' clikt-drawer__grid--text') + '">';

    /*
      Each value is a button. Where there is something worth saying about the
      material, an information marker sits beside the name and reveals a line
      of plain English underneath. It is a sibling of the button, not a child,
      because a button inside a button is invalid and gets dropped.
    */
    values.forEach(function (v) {
      /*
        A value is either a bare url or {src, hint}. The hint names the
        colour a custom-colour swatch was painted in, so the tile answers
        "what am I looking at" on hover instead of leaving it a mystery.
      */
      var entry = images[v.name];
      var src = entry && entry.src ? entry.src : entry;
      var hint = entry && entry.hint ? entry.hint : '';
      var note = noteFor(v.name);

      html += '<div class="clikt-tile-wrap' + (v.soldOut ? ' is-out' : '') + '">';
      html += '<button type="button" class="clikt-tile' +
        (v.checked ? ' is-on' : '') + (v.soldOut ? ' is-out' : '') +
        '" data-clikt-choose="' + v.id + '"' + (v.soldOut ? ' disabled' : '') + '>';
      if (withImages) {
        html += '<span class="clikt-tile__frame">' +
          (src
            ? '<img src="' + src + '" alt="" loading="lazy">'
            : '<span class="clikt-tile__noimage" aria-hidden="true"></span>') +
          '<span class="clikt-tile__tick" aria-hidden="true"></span>' +
        '</span>';
      }
      html += '<span class="clikt-tile__name">' + v.name + '</span>';
      if (hint) html += '<span class="clikt-tile__hint">' + hint + '</span>';
      if (v.soldOut) html += '<span class="clikt-tile__note">Unavailable</span>';
      html += '</button>';

      if (note) {
        html += '<button type="button" class="clikt-tile__info" data-clikt-note ' +
          'aria-expanded="false" aria-label="About ' + v.name + '">i</button>' +
          '<p class="clikt-tile__blurb" hidden>' + note + '</p>';
      }

      html += '</div>';
    });

    var body = d.querySelector('[data-clikt-drawer-body]');
    body.innerHTML = html + '</div>';

    /*
      A safety net for the pictures.

      The URLs are baked into the theme, so a photo deleted or replaced in
      the Shopify admin would otherwise leave a broken-image icon sitting in
      the drawer. If one fails to load it quietly becomes the same tinted
      placeholder used by finishes that were never photographed.
    */
    var shots = body.querySelectorAll('.clikt-tile__frame img');
    for (var i = 0; i < shots.length; i++) {
      shots[i].addEventListener('error', function () {
        var blank = document.createElement('span');
        blank.className = 'clikt-tile__noimage';
        blank.setAttribute('aria-hidden', 'true');
        if (this.parentNode) this.parentNode.replaceChild(blank, this);
      });
    }

    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('clikt-drawer-open');
    var close = d.querySelector('.clikt-drawer__close');
    if (close) close.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('clikt-drawer-open');
    if (activeFieldset) {
      var btn = activeFieldset.querySelector('[data-clikt-viewall]');
      if (btn) btn.focus();
    }
  }

  /* --- wiring ----------------------------------------------------------- */

  function init(scope) {
    var rows = (scope || document).querySelectorAll('.clikt-opt');
    for (var i = 0; i < rows.length; i++) sync(rows[i]);
  }

  /*
    Delegated, because Dawn replaces the whole picker on every variant
    change. Binding to the elements themselves would last exactly one click.
  */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-clikt-viewall]');
    if (!btn) return;
    e.preventDefault();
    var fieldset = btn.closest('.clikt-opt');
    if (fieldset) openDrawer(fieldset);
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('.clikt-opt input[type="radio"]');
    if (!input) return;
    var fieldset = input.closest('.clikt-opt');
    if (fieldset) sync(fieldset);
    /* Dawn re-renders after this; pick the labels up again once it has. */
    window.setTimeout(function () { init(document); }, 400);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /*
    Re-arm after Dawn swaps the section HTML.

    Scoped to the product info column, not the whole document, and throttled
    to one pass per frame. Watching everything meant every unrelated widget
    on the page woke this up.
  */
  var queued = false;
  var target = document.querySelector('.product__info-container') || document.body;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      init(document);
    });
  }).observe(target, { childList: true, subtree: true });
})();
